import type {
  PurchaseOrderDetailResponseType,
  PurchaseOrderUpdateType,
  PurchaseOrderPartyPayloadType,
  PurchaseOrderPartiesResponseType,
} from 'features/PurchaseOrder/@types/detail'
import type {
  PickingPreviewResponseType,
  PickingPreviewQueryType,
} from 'utils/apiList/supplyOrderPicking'
import type {
  GetHubFeatureFlagResponseType,
  GetFeatureFlagByLocationIdResponseType,
} from 'features/SupplyOrder/services/packingRecording'
import type { ProductV1Type } from 'features/Product/@types/Product'
import type { CustomerType } from 'features/BannerAndTicker/redux/banner/bannerSlice'
import type { PromotionQueryType, PromotionListResponseType } from 'utils/apiList/promotion'
/* eslint-disable import/no-duplicates */
/* eslint-disable @typescript-eslint/naming-convention */
import axios, { AxiosResponse } from 'axios'
import type {
  TickerType,
  TickerQueryType,
  TickerPayloadType,
  TickerListResponseType,
  TickerPageType,
  TickerLocationType,
} from 'utils/apiList/ticker'
import { Get, Put, Post, Delete } from 'config/apiServiceConfig'

import { objectToQueryParams } from 'utils/getParams'
import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import apiServiceApiGateway from 'config/apiServiceApiGateway'
import { getAvailableStaff } from 'features/Order/services/staffAvailability'
import type { GetBulkEditParamsResponseType } from 'utils/apiList/admin'

const { REACT_APP_BASE_URL_API, REACT_APP_BASE_URL_API_CONSUMER, REACT_APP_BASE_URL_KONG_API } =
  process.env
const baseUrl = REACT_APP_BASE_URL_API
const consumerAPI = REACT_APP_BASE_URL_API_CONSUMER

const uninterceptedAxiosInstance = axios.create()
const postValidateInboundEndpoint = (isHubFeatureEdDryScanActive: boolean): string =>
  `/api/${isHubFeatureEdDryScanActive ? 'v2/' : ''}inbound-grand/:id/validate`

export const getApi = (
  url: string,
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/${url}`, { params })

export const deleteSession = (): Promise<AxiosResponse> =>
  Delete({
    url: '/api/user/remove/session',
  })

export const postForgetPassword = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/request-forgot-password`, payload)

export const postUpdatePassword = (
  payload: Record<string, unknown> | FormData,
  header: Record<string, unknown>,
): Promise<AxiosResponse> =>
  uninterceptedAxiosInstance.post(`${baseUrl}/api/update-forgot-password`, payload, header)

export const getUserData = (): Promise<AxiosResponse> =>
  axios.get(`${REACT_APP_BASE_URL_KONG_API}/accounts/internal/v1/whoami`, {
    headers: {
      'x-device-id': 'desktop-admin',
    },
  })

// query appVersion={appVersion}&featureKey={featureKey}&userId={userId}
export const getFeatureFlagByLocationID = (
  locationId: string | number | null,
  query?: { [key: string]: string | number | boolean | undefined },
): Promise<
  AxiosResponse<
    GetFeatureFlagByLocationIdResponseType & {
      data: { features: { [key: string]: boolean | undefined } }
    }
  >
> => axios.get(`${baseUrl}/api/feature-flag/${locationId}/1`, { params: query })

export const getHubFeature = (
  params: { feature: string } | undefined,
): Promise<AxiosResponse<GetHubFeatureFlagResponseType>> =>
  axios.get(`${baseUrl}/api/feature-flag/hub-feature`, { params })

export const getAccountInternalFeatureFlagByLocationId = (
  params: Record<string, unknown>,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Get({
    url: `/accounts/internal/v1/hub-feature/device/location`,
    params,
  })

export const getFeatureFlag = (locationId: string, featureFlag: string): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/feature-flag/${locationId}/1/${featureFlag}`)

export const getInventoryStatusReason = (
  query: Record<string, unknown> | undefined,
): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/inventory-status-notes${objectToQueryParams(query)}`)

export const postCategory = (payload: Record<string, unknown> | FormData): Promise<AxiosResponse> =>
  axios.post(`${baseUrl}/api/category`, payload)

export const getCategories = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/category`, { params })

export const putCategory = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/category/${id}`, payload)

export const getCategory = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/category/${id}`)

export const getExportedCategoryLink = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/category/csv/download`)

export const getBanners = (query = ''): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/banner${query}`)

export const putBannerActive = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/banner/${id}/active`, payload)

export const getBanner = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/banner/${id}`)

export const getAllCategories = (): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/categories`)

export const getLocations = (query: string): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/location${query}`)

export const postLocation = (payload: Record<string, unknown> | FormData): Promise<AxiosResponse> =>
  axios.post(`${baseUrl}/api/location`, payload)

export const putLocationActive = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/location/${id}/active`, payload)

export const getLocation = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/location/${id}`)

export const putLocation = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/location/${id}`, payload)

export const getSupervisors = (type: string): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/user/supervisor/${type}`)

export const getAllCategoriesChild = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/categories/child`)

export const postProduct = (data: Record<string, unknown> | FormData): Promise<AxiosResponse> =>
  Post({ url: '/api/ims/:version/products', version: 'v2', data })

export const getProducts = (query = ''): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/product${query}`)

export type BoMProductOptionType = {
  productID: number
  productSkuNo: string
  productName: string
  baseUom: string | number
  bomCategory: string
}

export type GetProductsOnBoMResponseType = {
  data: BoMProductOptionType[]
}

export type GetProductsOnBoMParamsType = {
  pageSize?: number
  pageIndex?: number | string
  bomCategory?: string
  name?: string
}

export const getProductsOnBoM = (
  params: GetProductsOnBoMParamsType = {},
): Promise<AxiosResponse<GetProductsOnBoMResponseType>> =>
  Get<GetProductsOnBoMResponseType>({
    url: '/api/ims/:version/products',
    version: 'v2',
    params,
  })

export const getPpnList = (params: Record<string, unknown> | undefined): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/ims/ppn/v1`, { params })

export const putUpdateProductStatus = (
  id: string | number,
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Put({ url: `/ims/internal/v1/products/${id}/active`, data })

export type GetProductResponseType = { product_name: string }
export type ProductDetailResponseType = {
  product_id: number
  product_sku_number: string
  product_name: string
  base_uom?: { id: string | number }
  bom_category: { code: string }
}

export const getProduct = <ResponseType extends GetProductResponseType = ProductDetailResponseType>(
  id: string | number,
): Promise<AxiosResponse<ResponseType>> => axios.get(`${baseUrl}/api/product/${id}`)

export const getProductLabel = (
  productIds: string | number | string[] | number[],
): Promise<AxiosResponse> =>
  Get({
    url: '/api/product-label',
    params: { pageIndex: 0, pageSize: 1000, productIds },
  })

export const getDetailDiscoveryLabel = (labelId: string | number): Promise<AxiosResponse> =>
  Get({
    url: '/api/discovery-label/:labelId',
    urlParams: { labelId },
  })

export const deleteLabelOnProductApi = (
  productId: string | number,
  labelId: string | number,
): Promise<AxiosResponse> =>
  Delete({
    url: '/api/product-label/product/:productId/label/:labelId',
    urlParams: { productId, labelId },
  })

// export const putProduct = (id, payload) => axios.put(`${baseUrl}/api/product/${id}`, payload)
export const putProduct = (
  id: string | number,
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  Put({
    url: '/api/ims/:version/products/:id',
    version: 'v2',
    urlParams: { id },
    data,
  })

export const getInventory = (params: Record<string, unknown> | undefined): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/inventories/v4`, { params })
export const getInventoryV2 = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/ims/v2/inventories`, { params })

export type ProductAssignmentItemType = Omit<ProductV1Type, 'product_images'> & {
  product_active: boolean
}
export type ProductAssignmentPaginationType = {
  page_index: number
  page_size: number
  number_of_elements: number
}
export type ProductAssignmentResponseType = {
  data: ProductAssignmentItemType[]
  pagination: ProductAssignmentPaginationType
}
export type ProductAssignmentQueryType = { [key: string]: string | number | undefined }
export type ProductAssignmentPayloadType = {
  location_id: number | undefined
  product_ids: number[]
}

export const getInventoriesProduct = (
  params: ProductAssignmentQueryType | undefined,
): Promise<AxiosResponse<ProductAssignmentResponseType>> =>
  axiosInstanceApiGateway.Get<ProductAssignmentResponseType, true>({
    url: `/ims/internal/v1/products/new-inventory`,
    params,
  })

export const getHubs = (): Promise<AxiosResponse<WarehouseLocationType[]>> =>
  axios.get(`${baseUrl}/api/locations/hub`)

export const getLocationsByType = (type: string): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/locations/${type}`)

export const getOrderLocation = (): Promise<AxiosResponse<{ data: TickerLocationType[] }>> =>
  axios.get(`${baseUrl}/api/locations/order`)

export const getAlllocation = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/locations/all`)

export type BulkPriceProductType = {
  product_id: number
  product_name: string
  product_sku_number: string
}
export type BulkPriceLocationType = {
  location_id: number
  location_name: string
  location_type: string
}
export type BulkPricePayloadType = {
  productId: number
  locationIds: (string | number)[]
  price: number
}

export const getLocByProduct = (
  id: string | number,
): Promise<AxiosResponse<{ data: BulkPriceLocationType[] }>> =>
  axios.get(`${baseUrl}/api/inventory/product-locations/${id}`)

export const postInventory = (
  payload: ProductAssignmentPayloadType,
): Promise<AxiosResponse<unknown>> => axios.post(`${baseUrl}/api/inventory`, payload)

export const putInventoryBulkUpdatePrice = (
  payload: BulkPricePayloadType[],
): Promise<AxiosResponse<unknown>> => axios.put(`${baseUrl}/api/inventory/bulk-price`, payload)

export const putLocationInventoryActive = (
  locId: string,
  proId: string,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/inventory/${locId}/${proId}/active`, payload)

export const putLocationInventory = (
  locId: string,
  proId: string,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/inventory/${locId}/${proId}`, payload)

export interface WarehouseLocationType {
  location_id: number
  location_name: string
  location_type: string
  name?: string
  [key: string]: unknown
}
export const getWarehouse = () =>
  axios.get<WarehouseLocationType[]>(`${baseUrl}/api/locations/warehouse`)

export const getInventoriesWarehouse = (query: string): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/inventories/v2/warehouse`, { params: query })

export const getSupplyOrderProducts = (id: string | number, query = ''): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/supply-order/product/${id}${query}`)

export const postSupplyOrder = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/supply-order`, payload)

export const getSupplyOrder = (query = ''): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/supply-order${query}`)

export const getInbounds = (query = ''): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/inbound${query}`)

export const getInbound = (id: string | number = ''): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/inbound/${id}`)

export const putInbound = (
  id: string | number = '',
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/inbound/${id}`, payload)

export const getPrintSupplyOrder = (id: string | number = ''): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/supply-order/${id}/file`)

export const getPrintInbound = (id: string | number = ''): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/inbound/${id}/file`)

// DRIVER API'S
export const getDriverList_Driver = (query = ''): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/driver${query}`)

export const postNewDriver_Driver = (
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/driver`, data)

export const putDriver_Driver = (
  id: string | number,
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/driver/${id}`, data)

export const putDriverActive_Driver = (
  id: string | number,
  dataStatus: Record<string, unknown>,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/driver/${id}/active`, dataStatus)

export const getDriverAvailable = (locationId: string, role: string): Promise<AxiosResponse> =>
  apiServiceApiGateway.Post({
    url: `/staff-management/internal/v1/staff/get-by-role`,
    data: {
      role_name: `AUTH_${role}`,
      location_id: locationId,
    },
  })
// END DRIVER API's

// BUYER API's
export const getBuyerList_Buyer = (query = ''): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/customer${query}`)

export const putActiveBuyer_Buyer = (
  id: string | number,
  dataStatus: Record<string, unknown>,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/customer/${id}/active`, dataStatus)
// END BUYER API's

export type SupplyOrderCheckQtyItemType = {
  supply_order_item_id: number
  product_sku: string
  product_name: string
  product_rack: string
  product_request_quantity: number
  product_actual_quantity: string | number
  product_note: string
}

export type SupplyOrderDetailCheckQtyType = {
  supply_order_id: number
  supply_order_number: string
  supply_order_items: SupplyOrderCheckQtyItemType[]
}

export const getSupplyOrderDetail = (
  id: string | number = '',
): Promise<AxiosResponse<SupplyOrderDetailCheckQtyType>> =>
  axios.get(`${baseUrl}/api/supply-order/${id}`)

export const putSupplyOrder = (
  id: string | number = '',
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/supply-order/${id}`, payload)

export const putSupplyOrderConfirm = (
  id: string | number = '',
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/supply-order/${id}/confirm`, payload)

export const getPickers = (params: Record<string, unknown> | undefined): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/picker-packer-order`, { params })

export const getPickersSuper = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/picker/super/order`, { params })

export const getPicker = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/picker/order/${id}`)

export const getPickerSuper = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/picker/super/order/${id}`)

export const putPicker = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/picker/order/${id}/picking`, payload)

export const putPickerSuper = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/picker/super/order/${id}/picking`, payload)

export const changePicker = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/picker/order/${id}/change-picker`, payload)
export const changePickerSuper = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/picker/super/order/${id}/change-picker`, payload)

export const changePacker = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/packer/order/${id}/change-packer`, payload)
export const changePackerSuper = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/packer/super/order/${id}/change-packer`, payload)

export const cancelPicker = (
  id: string | number,
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/picker/order/${id}/cancel`, data)

export const returnPicker = (id: string | number): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/picker/order/${id}/return`)

export const getPackers = (params: Record<string, unknown> | undefined): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/picker-packer-order`, { params })

export const getOrdersV2 = (params: Record<string, unknown> | undefined): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/picker-packer-order`, { params })

export const getPackersSuper = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/packer/super/order`, { params })

export const putPacker = (id: string | number): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/picker/order/${id}/packing`)

export const getPacker = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/packer/order/${id}`)

export const getPackerInvoice = (query: string): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/packer/order/detail${query}`)
export const getPackerOrderId = (query: string): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/packer/order/detail${query}`)
export const getPackerOrderIdSuper = (
  orderId: Record<string, unknown>,
  query: string,
): Promise<AxiosResponse> => {
  const id = orderId.orderId ? orderId.orderId : orderId
  return axios.get(`${baseUrl}/api/packer/super/order/${id}/item${query}`)
}

export const postPackerInvoice = (query: string): Promise<AxiosResponse> =>
  axios.post(`${baseUrl}/api/packer/order/packing`, query)

export const postPackerInvoiceSuper = (query: Record<string, unknown>): Promise<AxiosResponse> =>
  axios.post(`${baseUrl}/api/packer/super/order/${query.order_id}/packing`, query)

export const getTimeslotList = (query: string): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/order/timeslots${query}`)

export const putPackerQty = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/packer/order/${id}/confirm-packing`, payload)

export const putPackerQtySuper = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/packer/super/order/${id}/confirm-packing`, payload)

export const cancelAutoAssign = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/packer/order/${id}/cancel-driver`, payload)
export const cancelPacker = (
  id: string | number,
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/packer/order/${id}/cancel`, data)

export const getSkuManualInputNotes = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/sku-manual-input-notes`, { params })

export const postSkuManualInputAuth = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/sku-manual-input-auth`, payload)

export const getDrivers = (id: string | number): Promise<AxiosResponse> => getAvailableStaff(id)
export const getDriversSuper = (id: string | number, _query: string): Promise<AxiosResponse> =>
  getAvailableStaff(id, 'SUPER_DRIVER')

export const putCallDriver = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/packer/order/${id}/assign-driver`, payload)

export const putChangeDriver = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/packer/order/${id}/assign-driver`, payload)

export const putPickupOrder = (id: string | number): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/packer/order/${id}/pick-up`)

export const returnPacker = (id: string | number): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/packer/order/${id}/return`)

export const listPacker = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/packer/order/${id}/print-list`)

export const listP1cker = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/picker/order/${id}/print-list`)

export const listP1ckerSuper = (
  id: string | number,
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/picker/super/order/${id}/item`, { params })

export const putUpcomingOrder = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  Put({ url: `/api/picker/order/${id}/upcoming-to-new-order`, data: payload })

export const resiPacker = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/packer/order/${id}/print-receipt`)

export const detailOrder = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/packer/order/${id}`)

export const getSettingsData = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/order-setting`)

export const putSettingsData = (
  id: string | number,
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/order-setting/${id}`, data)

export const getTickers = (
  params: TickerQueryType | undefined,
): Promise<AxiosResponse<TickerListResponseType>> => axios.get(`${baseUrl}/api/ticker`, { params })

export const postTicker = (payload: TickerPayloadType): Promise<AxiosResponse<unknown>> =>
  axios.post(`${baseUrl}/api/ticker`, payload)

export const putTicker = (
  id: number | undefined,
  payload: TickerPayloadType,
): Promise<AxiosResponse<unknown>> => axios.put(`${baseUrl}/api/ticker/${id}`, payload)

export const getTicker = (id: number): Promise<AxiosResponse<TickerType>> =>
  axios.get(`${baseUrl}/api/ticker/${id}`)

export const postPickerPooled = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/picker/order/pooled`, payload)

export const postPickerTagPool = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/picker/order/tag-pool`, payload)

export const postPickerUntagPool = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/picker/order/untag-pool`, payload)

export const postPackerPooled = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/packer/order/pooled`, payload)

export const postPackerTagPool = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/packer/order/tag-pool`, payload)

export const postPackerUntagPool = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/packer/order/untag-pool`, payload)

export const putTickerStatus = (
  id: number,
  payload: { is_active: boolean },
): Promise<AxiosResponse<unknown>> => axios.put(`${baseUrl}/api/ticker/${id}/active`, payload)

export const getPages = (): Promise<AxiosResponse<TickerPageType[]>> =>
  axios.get(`${baseUrl}/api/funnel-pages`)

export const getAppLinks = (): Promise<AxiosResponse<string[]>> =>
  axios.get(`${baseUrl}/api/app-link`)

export type RefferalOptionType = { name: string; value?: string | number }
export type RefferalCustomerType = CustomerType & { name: string }
export type RefferalCustomerQueryType = {
  number?: string
  pageIndex?: number
  pageSize?: number
  newBuyer?: boolean
}
export type RefferalCustomerResponseType = {
  content: CustomerType[]
  size: number
  totalElements: number
  totalPages: number
  number: number
  numberOfElements: number
}
export type RefferalSettingType = {
  id: number
  benefit_type: string
  targeting_type: string
  mechanism_type: string
  quota: string | number
  minimum_purchase: string | number
  amount: string | number
  discount_percentage: string | number
}
export type RefferalSettingPayloadType = Omit<RefferalSettingType, 'id'>

export const getBenefits = (): Promise<AxiosResponse<RefferalOptionType[]>> =>
  axios.get(`${baseUrl}/api/voucher/benefit`)
export const getTargets = (): Promise<AxiosResponse<RefferalOptionType[]>> =>
  axios.get(`${baseUrl}/api/voucher/targeted`)

export const getPaymentChannel = (): Promise<AxiosResponse> =>
  axios.get(`${consumerAPI}/api/internal/payment-channel`)

export const getMechanisms = (): Promise<AxiosResponse<RefferalOptionType[]>> =>
  axios.get(`${baseUrl}/api/voucher/mechanism`)

export const getCustomer = (
  params: RefferalCustomerQueryType | undefined,
): Promise<AxiosResponse<RefferalCustomerResponseType>> =>
  axios.get(`${baseUrl}/api/customer/search`, { params })

export const getRefferalSettingBuyer = (): Promise<AxiosResponse<RefferalSettingType>> =>
  axios.get(`${baseUrl}/api/referral-setting-buyer`)

export const putRefferalSettingBuyer = (
  id: string | number,
  payload: RefferalSettingPayloadType,
): Promise<AxiosResponse<RefferalSettingType>> =>
  axios.put(`${baseUrl}/api/referral-setting-buyer/${id}`, payload)

export const postVoucher = (payload: Record<string, unknown> | FormData): Promise<AxiosResponse> =>
  axios.post(`${baseUrl}/api/voucher`, payload)

export const getVoucher = (
  params: PromotionQueryType | undefined,
): Promise<AxiosResponse<PromotionListResponseType>> =>
  axios.get(`${baseUrl}/api/voucher`, { params })

export const putVoucherActive = (
  id: string | number,
  payload: { is_active: boolean },
): Promise<AxiosResponse<unknown>> => axios.put(`${baseUrl}/api/voucher/${id}/active`, payload)

export const getVoucherDetail = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/voucher/${id}`)

export const putVoucherDetail = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/voucher/${id}`, payload)

export const getOrderSettingDetail = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/order-setting-detail`)

export const putOrderSetting = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/order-setting-detail/${id}`, payload)

export const postUploadedTargettedUSer = (
  payload: FormData,
): Promise<AxiosResponse<CustomerType[]>> =>
  axios.post(`${baseUrl}/api/voucher/csv/upload-user`, payload)

export const postUom = (payload: Record<string, unknown> | FormData): Promise<AxiosResponse> =>
  axios.post(`${baseUrl}/api/uoms`, payload)
export const getUoms = (params: Record<string, unknown> | undefined): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/uoms`, { params: { ...params, pageSize: 50 } })
export const putUom = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/uoms/${id}/toggle`, payload)
export const putUomData = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/uoms/${id}`, payload)
export const getUomsExport = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/uoms/export`, { params })
export const getUomGroups = (): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/uoms/groups`)
export const getProductPackageStructure = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/product-package-structure`, { params })
export const putProductPackageStructure = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/product-package-structure`, payload)
export const deleteProductPackageStructure = (id: string | number): Promise<AxiosResponse> =>
  axios.delete(`${baseUrl}/api/product-package-structure/${id}`)
export const getRackStorage = (
  params: Record<string, unknown> | undefined = {},
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/racks/storage`, { params })

// These legacy placeholders ignore their arguments and still call /api/uoms.
// The nested data is never rendered or stored by the PutAway actions.
export type LegacyPutAwayResponseType = { data: unknown }
export type PutAwaySearchParamsType = {
  vendor: string
  dateFrom: string
  dateTo: string
  poNumber: string
}
export const getPutAway = (_params?: unknown): Promise<AxiosResponse<LegacyPutAwayResponseType>> =>
  axios.get(`${baseUrl}/api/uoms`)
export const getGrn = (): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/uoms`)
export const getDamagePutAway = (
  _params?: unknown,
): Promise<AxiosResponse<LegacyPutAwayResponseType>> => axios.get(`${baseUrl}/api/uoms`)
export const getReceiving = (): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/uoms`)
export const getVendor = (_params?: unknown): Promise<AxiosResponse<LegacyPutAwayResponseType>> =>
  axios.get(`${baseUrl}/api/uoms`)

export type PurchaseOrderQueryType = { [key: string]: string | number | boolean | undefined }
export type PurchaseOrderItemType = {
  id: number
  numberPo: string
  netsuiteId: string | number
  vendorName: string
  createdAt: string | number
  quantity: number
  totalQuantity: number
  status: string
}
export type PurchaseOrderVendorType = { id: number; companyName: string }
export type PurchaseOrderVendorOptionType = PurchaseOrderVendorType & { name: string }
export type PurchaseOrderListResponseType = {
  data: { purchaseOrders: PurchaseOrderItemType[] }
  pagination: {
    pageSize: number
    totalData: number
    pageTotal: number
    lastCursor: string | number
  }
}
export type PurchaseOrderPaginationType = {
  pageSize: number
  totalData: number
  totalPage: number
  currentPage: string | number
  currentItem: number
}
export type PurchaseOrderOptionType = { name: string; id: string }

export const getPurchaseOrders = (
  params: PurchaseOrderQueryType | undefined,
): Promise<AxiosResponse<PurchaseOrderListResponseType>> =>
  axios.get(`${baseUrl}/erp/api/purchase-orders`, { params })

export const cancelPurchaseOrders = (id: string | number): Promise<AxiosResponse<unknown>> =>
  axios.get(`${baseUrl}/erp/api/purchase-orders/cancel/${id}`)

export const closedPurchaseOrders = (id: string | number): Promise<AxiosResponse<unknown>> =>
  axios.get(`${baseUrl}/erp/api/purchase-orders/close/${id}`)

export const detailPurchaseOrder = (
  id: string | number | undefined,
  params: PurchaseOrderQueryType | undefined,
): Promise<AxiosResponse<PurchaseOrderDetailResponseType>> =>
  axios.get(`${baseUrl}/erp/api/purchase-orders/${id}`, { params })

export const putPurchaseOrder = (
  payload: PurchaseOrderUpdateType,
  id: string | number | undefined,
): Promise<AxiosResponse<unknown>> => axios.put(`${baseUrl}/erp/api/purchase-orders/${id}`, payload)

export const getPartiesPurchaseOrder = (params: {
  purchaseOrderId: string | number | undefined
}): Promise<AxiosResponse<PurchaseOrderPartiesResponseType>> =>
  axios.get(`${baseUrl}/erp/api/purchase-orders/order-parties`, { params })
export const postPartiesPurchaseOrder = (
  payload: PurchaseOrderPartyPayloadType,
): Promise<AxiosResponse<unknown>> =>
  axios.post(`${baseUrl}/erp/api/purchase-orders/order-parties`, payload)

export const deletePartiesPurchaseOrder = (id: string | number): Promise<AxiosResponse<unknown>> =>
  axios.delete(`${baseUrl}/erp/api/purchase-orders/order-parties/${id}`)

export const putPartyPurchaseOrder = (
  id: string | number | undefined,
  payload: PurchaseOrderPartyPayloadType,
): Promise<AxiosResponse<unknown>> =>
  axios.put(`${baseUrl}/erp/api/purchase-orders/order-parties/${id}`, payload)

export const getVendorPo = (): Promise<AxiosResponse<{ data: PurchaseOrderVendorType[] }>> =>
  axios.get(`${baseUrl}/erp/api/vendor`)

export const getPurchaseOrderList = (qs: string): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Get({
    url: `/wms/internal/v1/purchase-order?${qs}`,
  })

export const getPurchaseOrdersReference = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/purchase-order/reference`, { params })

export const createPoReference = (poId: string): Promise<AxiosResponse> =>
  Post({
    url: '/api/wms/:version/purchase-order/reference/:poId',
    version: 'v1',
    urlParams: { poId },
  })

export const getVendorPurchaseOrder = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/purchase-order/vendor`, { params })

export const getVendorList = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Get({
    url: `/commercial/internal/v1/vendors`,
    params,
  })

export const postPurchaseOrderItemScan = (
  poId: string,
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Post({ url: `/wms/internal/v1/purchase-order/${poId}/item-scan`, data })

export const putPurchaseOrderLite = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/purchase-order/${id}`, payload)

export const getPurchaseOrderCsvTemplate = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/purchase-order/csv/template`)

export const postPurchaseOrderCsv = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/purchase-order/csv`, payload)

export const postPurchaseOrderStatus = (
  id: string | number,
  status: string,
): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/purchase-order/${id}/status?status=${status}`)

export const getInboundPo = (params: Record<string, unknown> | undefined): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/purchase-order/inbound`, { params })

export const getPurchaseOrderDetailLiteInbound = (
  id: string | number,
  query: string,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/purchase-order/inbound/${id}${query}`)

export const putPurchaseOrderDetailLiteInbound = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/wms/v2/purchase-order/inbound/${id}`, payload, {
    timeout: 90000,
  })

export const putPurchaseOrderSubmitCheckQty = (
  id: string | number,
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Put({
    url: `/wims/internal/v1/purchase-orders/inbound/${id}`,
    data,
    config: {
      timeout: 90000,
    },
  })

export const getInboundPoDataPrint = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/purchase-order/${id}/grn`)

export const getRacksExport = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/racks/export`, { params })

export const getSupplyOrderRemarks = (): Promise<AxiosResponse> =>
  Get({
    url: '/api/wms/v2/supply-order/remarks',
  })

export const getSupplyOrderGrand = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/supply-order-grand`, { params })

export const getSupplyOrderGrandV2 = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => Get({ url: '/api/wms/:version/supply-order', version: 'v2', params })

export const getSupplyOrderType = (): Promise<AxiosResponse> =>
  Get({ url: '/api/wms/v2/supply-order/type' })

export const getSupplyOrderGrandProduct = (
  id: string | number,
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/supply-order-grand/product/${id}`, {
    params,
  })

export const startSoLoadingActivity = (
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  apiServiceApiGateway.Put({ url: '/wms/internal/v1/supply-order/loading/start-activity', data })

export const postSupplyOrderGrand = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/wms/v2/supply-order`, payload)

export const getSupplyOrderGrandDetailItem = (
  id: string | number,
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/supply-order-grand/${id}/item`, { params })

export const putSupplyOrderGrand = (
  payload: Record<string, unknown> | FormData,
  id: string | number,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/wms/v2/supply-order/${id}`, payload)

export const getSupplyOrderGrandData = (
  id: string | number,
  params: PickingPreviewQueryType | undefined,
): Promise<AxiosResponse<PickingPreviewResponseType>> =>
  axios.get(`${baseUrl}/api/supply-order-grand/${id}/data`, { params })

export const getPrintSupplyOrderGroup = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/wms/v1/supply-order/loading/${id}/file`)

export const putSupplyOrderGrandStatus = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  Put({
    url: '/api/wms/:version/supply-order/:id/status',
    version: 'v2',
    urlParams: { id },
    data: payload,
  })

export const postStartUnloadingActivity = (unloadingId: string): Promise<AxiosResponse> =>
  Post({
    url: '/api/wms/:version/supply-order/unloading/start/:unloadingId',
    version: 'v1',
    urlParams: { unloadingId },
    data: {
      device_type: 'WEB',
    },
  })

export const postSupplyOrderGrandCopy = (id: string | number): Promise<AxiosResponse> =>
  axios.post(`${baseUrl}/api/supply-order-grand/${id}/copy`)

export const getSupplyOrderGrandPacking = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/supply-order-grand/${id}/packing`)

export const getSupplyOrderDeliveryDocument = (query: string): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/supply-order-grand/delivery-document${query}`)

export const getSupplyOrderDeliveryDocumentLoadingV2 = (query: string): Promise<AxiosResponse> =>
  Get({
    url: `/api/wms/:version/supply-order/print-delivery-document${query}`,
    version: 'v2',
  })

export const getSupplyOrderDeliveryDocumentLoading = (
  id: string | number,
): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/wms/v2/supply-order/print-delivery-document/${id}`)

export const putSupplyOrderGrandPacking = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/supply-order-grand/${id}/packing`, payload)

export const getInboundSoV2 = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> =>
  Get({ url: '/api/wms/:version/supply-order-inbound', version: 'v2', params })

export const getManufacturingOrder = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Get({ url: '/ims/internal/v1/manufacturing/order', params })

export const getInboundSOCheckQty = (id: string | number): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Get({
    url: `/wms/internal/v1/supply-order/${id}/inbound/check-quantity`,
  })

export const getInboundsSOItem = (
  id: string | number,
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> =>
  Get({
    url: `/api/wms/v3/supply-order-inbound/${id}/item/product`,
    urlParams: { id },
    params,
  })

export const putInboundsSOItem = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/wms/v2/supply-order-inbound/${id}`, payload)

export const getGrnData = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/inbound-grand/${id}/grn`)

export const postInboundPutaway = (
  status: string,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/inbound-grand/putaway/${status}`, payload)

export const postValidateCheckQty = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axios.post(`${baseUrl}/api/supply-order-grand/${id}/packing/validate`, payload)

export const getScanManualReason = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/supply-order-grand/manual-reason`)

export const getStatus = (): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/inventory-status`)

export const putExpiryDateInboundItem = (
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Put({
    url: `/wms/internal/v1/supply-order/inbound/item/expiry-date`,
    data,
  })

export const postValidateInbound = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
  isHubFeatureEdDryScanActive = false,
): Promise<AxiosResponse> =>
  Post({
    url: postValidateInboundEndpoint(isHubFeatureEdDryScanActive),
    urlParams: { id },
    data: payload,
  })

export const postSubmitInboundItem = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Post({
    url: `/wms/internal/v1/supply-order/:id/inbound/item`,
    urlParams: { id },
    data: payload,
  })

export const getInboundItemEdit = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/inbound-grand/item/edit`, { params })

export const putInboundItemEdit = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/inbound-grand/item/edit`, payload)

export const deleteInboundItem = (queryParams: string): Promise<AxiosResponse> =>
  apiServiceApiGateway.Delete({
    url: `/wms/internal/v1/supply-order/inbound/item${queryParams}`,
  })

export const getRacks = (
  id: string | number,
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/racks/${id}`, { params })

export const putRackInboundItem = (
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  apiServiceApiGateway.Put({ url: '/wms/internal/v1/supply-order/inbound/item/rack', data })

export const getAllStatusSource = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/product/status-source`)

export const getAllStorageHandling = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/product/storage-handling`)

export const getAllBrand = ({
  params,
}: {
  params: Record<string, unknown> | undefined
}): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/brand`, { params })

export const getAllBrandV2 = ({
  params,
}: {
  params: Record<string, unknown> | undefined
}): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/v2/brand`, { params })

export const getAllBOMCategory = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/product/bom-category`)
export const getOrderKit = (params: Record<string, unknown> | undefined): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/order-kit`, { params })

export interface CataloguePayloadType {
  category_name?: string
  category_image?: string | null
  superImage?: string | null
  category_position?: number
  category_twenty_one?: boolean
  category_hidden?: boolean
  for_discount?: boolean
  category_active?: boolean
  targeted_user_type?: number
  list_user_id?: number[]
  banners?: Record<string, unknown>[]
  catalogueType?: number
  destination_url?: string
  category_image_new?: Record<string, unknown> | null
  category_image_old?: Record<string, unknown> | null
  category_label?: string
  category_segmentations?: { type_id: number; segment_type_id: number }[]
  curated_product_criteria?: unknown
  category_image_tabbing?: Record<string, unknown> | null
  vendors?: unknown
  iconImage?: string | null
  category_childs?: Record<string, unknown>[]
  [key: string]: unknown
}

export const postCatalogue = (payload: CataloguePayloadType | FormData) =>
  axios.post<GetCatalogueResponseType>(`${baseUrl}/api/catalogue`, payload)
export const getOrderKitComponents = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/order-kit/${id}/components`)

export const getOrderKitDetail = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/order-kit/${id}`)

export const postOrderKit = (patload: Record<string, unknown>): Promise<AxiosResponse> =>
  axios.post(`${baseUrl}/api/order-kit`, patload)

export const getFullfllmentOrderKit = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/order-kit/${id}/fulfillments`)

export const cancelOrderKit = (id: string | number): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/order-kit/${id}/cancellation`)

export const confirmOrderKit = (id: string | number): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/order-kit/${id}/confirmation`)

export const putOrderKit = (
  payload: Record<string, unknown> | FormData,
  id: string | number,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/order-kit/${id}`, payload)

export const getKittingPickingList = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/order-kit/${id}/picking-list`)

export const getCheckQtyKitting = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/order-kit/${id}/checking-list`)

export const pickOrderKit = (id: string | number): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/order-kit/${id}/pick`)

export const postQtyValidate = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/order-kit/${id}/qty-validation`, payload)

export const postDetailBulk = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/product-detail/bulk`, payload)

export const postOrderKitActual = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/order-kit/${id}/actual-qty`, payload)

export const putOrderKitStatus = (id: string | number): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/order-kit/${id}/kit`)

export const putOrderKitCompletion = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/order-kit/${id}/completion`, payload)

export const putOrderKitSubmit = (
  id: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/order-kit/${id}/submit`, payload)

export interface CatalogueChildResponseType {
  category_id: number
  category_name?: string
  category_position: number
  total_position: number
  category_image: string
  category_active: boolean
  is_all_day: boolean
  iconUrl: string
  catalogueType: number
  start_date: string | null
  end_date: string | null
  start_time: string | null
  end_time: string | null
  curated_product_criteria: unknown
  vendors: unknown
  banners: unknown[]
  category_childs: unknown[]
  [key: string]: unknown
}

export interface GetCatalogueResponseType {
  category_id: number
  category_name?: string
  category_position: number
  total_position: number
  category_image: string
  category_active: boolean
  category_hidden: boolean
  for_discount: boolean
  is_all_day: boolean
  iconUrl: string
  catalogueType: number
  start_date: string | null
  end_date: string | null
  start_time: string | null
  end_time: string | null
  category_childs?: CatalogueChildResponseType[]
  curated_product_criteria: unknown
  vendors: unknown
  banners: unknown[]
  [key: string]: unknown
}

export interface GetCataloguesResponseType {
  numberOfElements: number
  totalElements: number
  totalPages: number
  size: number
  number: number
  content: GetCatalogueResponseType[]
}

export const getCatalogs = (params: Record<string, unknown> | undefined): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/catalogue`, { params })

export const putCatalogue = (
  id: string | number,
  payload: CataloguePayloadType | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/catalogue/${id}`, payload)

export const getLabelOrderProduct = ({
  orderID,
  productID,
}: {
  orderID: string
  productID: string
}): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/label/${orderID}/${productID}`)

export const getDataGeneratedKoli = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/kolis`, { params })

export const getDetailDataGeneratedKoli = (
  date: string,
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/kolis/${date}`, { params })

export const postGenerateKoli = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.post(`${baseUrl}/api/kolis`, payload)
export const putBrandCloudKitchen = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> => axios.put(`${baseUrl}/api/brand/cloud-kitchen`, payload)

export const getBrandCompany = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => axios.get(`${baseUrl}/api/brand-companies`, { params })

export const postBrand = (payload: Record<string, unknown> | FormData): Promise<AxiosResponse> =>
  axios.post(`${baseUrl}/api/brand`, payload)

export const putBrand = (payload: Record<string, unknown> | FormData): Promise<AxiosResponse> =>
  axios.put(`${baseUrl}/api/brand`, payload)
export const getTargetingType = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/targeting-type`)

export const getCatalogueById = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/catalogue/${id}`)

export const getSupplyOrderGrandKoliLabel = (id: string | number): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/v2/supply-order-grand/${id}/koli-label`)

export const downloadLocationReRouteLog = (
  id: string | number,
  startDate: string,
  endDate: string,
): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/location/${id}/history/reroute`, {
    responseType: 'blob',
    params: {
      start_date: startDate,
      end_date: endDate,
    },
  })

export const getFeatureAndServiceIdBulkUpload = (
  params: { code: string } | undefined,
): Promise<AxiosResponse<GetBulkEditParamsResponseType>> =>
  axios.get(`${baseUrl}/api/bulk-upload/service-feature`, { params })
export const getLocationType = (): Promise<AxiosResponse> =>
  axios.get(`${baseUrl}/api/location/type`)
export const getMapPlaceDetail = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> => Get({ url: `/api/maps/place-detail`, params })
export const getLocationCloseReason = (): Promise<AxiosResponse> =>
  Get({ url: `/api/location/close-reason` })
export const getInProgressPicking = (id: string | number): Promise<AxiosResponse> =>
  Get({ url: `/api/supply-order-grand/${id}/in-progress-picking` })

export const getLocationPoolingConfig = (locationId: string): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Get({ url: `/oms/internal/v1/pooling_configurations/${locationId}` })
export const getCombinationSLA = (params: string | undefined): Promise<AxiosResponse> =>
  Get({
    url: `/api/pooling-configuration/sla-combination?combination=${params}`,
  })
export const putSavePoolingConfig = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Put({
    url: `/oms/internal/v1/pooling_configuration`,
    data: payload,
  })
export const postNewPeakHourPoolingConfig = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Post({
    url: `/oms/internal/v1/pooling_configuration/peak_time`,
    data: payload,
  })
export const putNewPeakHourPoolingConfig = (
  payload: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Put({
    url: `/oms/internal/v1/pooling_configuration/peak_time`,
    data: payload,
  })

export const getReasonsForSupplyOrder = (
  params: Record<string, unknown> | undefined,
): Promise<AxiosResponse> =>
  Get({ url: `/api/wms/:version/purchase-order/reasons`, version: 'v2', params })

export const fetchOrderDetailV2 = (id: string | number): Promise<AxiosResponse> =>
  Get({ url: `/api/v2/order/${id}` })
export const fetchOrderActivities = (id: string | number): Promise<AxiosResponse> =>
  Get({ url: `/api/v2/order/${id}/activities` })

export const getStaffManagement = (
  locationId: string | number,
  role: string,
  _options?: { locationType: string },
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Post({
    url: `/staff-management/internal/v1/staff/get-by-role`,
    data: {
      role_name: role,
      location_id: locationId,
    },
  })

export const getSoConversionItems = (supplyOrderId: string): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Get({
    url: `/wms/internal/v1/supply-order/conversion/items/${supplyOrderId}`,
  })

export const applySoConversion = (
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  axiosInstanceApiGateway.Post({
    url: '/wms/internal/v1/supply-order/conversion/task',
    data,
  })

export const getRouteMaster = (query: Record<string, unknown> | string): Promise<AxiosResponse> =>
  Get({
    url: `/api/wms/:version/route-master${objectToQueryParams(query)}`,
    version: 'v1',
  })

export const postAssignPicker = (
  soId: string | number,
  data: Record<string, unknown> | FormData,
): Promise<AxiosResponse> =>
  Post({
    url: `/api/wms/:version/supply-order/${soId}/picking/assign`,
    version: 'v2',
    data,
  })
